import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { House, Users, Gear, SignOut, CaretRight, ChartBar, Cpu, Wrench, Tag, Shield, Star, Briefcase, Ticket, CurrencyDollar, Buildings, Airplane, ClockCounterClockwise, Megaphone, GearSix, Wallet, UserList, AddressBook, HandCoins, Monitor, Target, Package, Handshake, Storefront, ShoppingCart, Calculator, Archive, ChartPie, Receipt, Truck, CreditCard, ChatCircleDots } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export const Sidebar = ({ activeTab, setActiveTab, className = "" }) => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [clientHasPolicy, setClientHasPolicy] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [posOpen, setPosOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  const handleLogout = () => { logout(); navigate("/login"); };

  useEffect(() => {
    if ((user?.role === "cliente" || user?.role === "comercial" || user?.role === "auxiliar" || user?.role === "marketing" || user?.role === "subgerente") && token) {
      const checkPolicy = async () => {
        try {
          const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
          if (meRes.data.policy_type_name) { setClientHasPolicy(true); return; }
          if (user?.role === "cliente") {
            const ticketsRes = await axios.get(`${API}/tickets`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }));
            if (ticketsRes.data.some(t => t.client_has_policy)) setClientHasPolicy(true);
          }
        } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
      };
      checkPolicy();
    }
  }, [user, token]);

  // Chat unread polling
  useEffect(() => {
    if (!token) return;
    const fetchUnread = () => {
      axios.get(`${API}/chat/unread`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setChatUnread(r.data?.unread || 0))
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [token, activeTab]);

  const navItems = [
    { id: "dashboard", label: "TEAM", icon: House, roles: ["admin", "ingeniero", "cliente", "comercial", "auxiliar", "marketing", "subgerente"] },
    { id: "tickets", label: "Tickets", icon: Ticket, roles: ["admin", "ingeniero", "cliente", "auxiliar", "comercial", "subgerente"] },
    { id: "ticket-monitor", label: "Monitor", icon: Monitor, roles: ["admin", "ingeniero", "auxiliar", "subgerente"] },
    { id: "comercial", label: "Comercial", icon: Briefcase, roles: ["admin", "comercial", "ingeniero", "auxiliar", "subgerente"] },
    { id: "negociaciones", label: "Proyectos", icon: Handshake, roles: ["admin", "comercial", "ingeniero", "auxiliar", "subgerente"] },
    { id: "marketing", label: "Marketing", icon: Megaphone, roles: ["admin", "comercial", "auxiliar", "marketing", "subgerente"] },
    { id: "quotations", label: "Cotizaciones", icon: CurrencyDollar, roles: ["admin", "comercial", "subgerente", "ingeniero", "auxiliar", "marketing"] },
    { id: "viaticos", label: "Viaticos", icon: Airplane, roles: ["admin", "ingeniero", "comercial", "auxiliar", "subgerente"] },
    { id: "kpi", label: "KPIs", icon: Target, roles: ["admin", "ingeniero", "comercial", "auxiliar", "marketing", "subgerente"] },
    { id: "chat", label: "Chat", icon: ChatCircleDots, roles: ["admin", "ingeniero", "comercial", "auxiliar", "marketing", "subgerente", "cliente"], badge: chatUnread },
    { id: "productos", label: "Productos", icon: Package, roles: ["admin", "ingeniero", "comercial", "auxiliar", "marketing", "subgerente", "cliente"] },
  ];

  const adminItems = [
    { id: "admin-finanzas", label: "Finanzas", icon: Wallet },
    { id: "admin-rrhh", label: "Recursos Humanos", icon: UserList },
    { id: "admin-clientes", label: "Clientes", icon: Users },
  ];

  const configItems = [
    { id: "users", label: "Usuarios", icon: Users },
    { id: "announcements", label: "Avisos", icon: Megaphone },
    { id: "reports", label: "Reportes", icon: ChartBar },
    { id: "audit-log", label: "Bitacora", icon: ClockCounterClockwise },
  ];

  const serviceItems = [
    { id: "hardware-types", label: "Hardware", icon: Cpu },
    { id: "software-types", label: "Software", icon: Wrench },
    { id: "status-types", label: "Estatus", icon: Tag },
    { id: "policy-types", label: "Polizas", icon: Shield },
  ];

  const posItems = [
    { id: "pos-dashboard", label: "Dashboard", icon: ChartPie },
    { id: "pos-ventas", label: "Ventas", icon: ShoppingCart },
    { id: "pos-compras", label: "Compras", icon: Truck },
    { id: "pos-historial", label: "Historial", icon: Receipt },
    { id: "pos-creditos", label: "Cuentas x Cobrar", icon: CreditCard },
    { id: "pos-productos", label: "Inventario", icon: Package },
    { id: "pos-reportes", label: "Reportes", icon: ChartBar },
    { id: "pos-corte", label: "Resumen del Dia", icon: Calculator },
    { id: "pos-config", label: "Configuracion", icon: GearSix },
  ];

  const isServiceTab = serviceItems.some(s => s.id === activeTab);
  const isConfigTab = configItems.some(c => c.id === activeTab);
  const isAdminTab = adminItems.some(a => a.id === activeTab);
  const isPosTab = posItems.some(p => p.id === activeTab);

  return (
    <aside className={`sidebar ${className}`} data-testid="sidebar">
      <div className="flex items-center gap-3 mb-8">
        <img src="/logo-maxipos.png" alt="MaxTicket" className="w-10 h-10 object-contain rounded" />
        <span className="font-bold text-xl">MaxTicket</span>
      </div>
      <div className="mb-6 p-3 bg-slate-800/50">
        <p className="text-sm text-slate-400">Sesion activa</p>
        <p className="font-medium text-white truncate">{user?.name}</p>
        <Badge className="mt-1 bg-blue-600/20 text-blue-400 border-0 text-xs uppercase">{user?.role}</Badge>
        {(clientHasPolicy && (user?.role === "cliente" || user?.role === "comercial")) && (
          <div className="mt-2 flex items-center gap-1.5 px-2 py-1 bg-amber-500/15 border border-amber-500/30 rounded" data-testid="sidebar-5-star-badge">
            <div className="flex -space-x-0.5">
              {[...Array(5)].map((_, i) => (<Star key={i} size={12} weight="fill" className="text-amber-400" />))}
            </div>
            <span className="text-[10px] font-bold text-amber-300 tracking-wider">5 ESTRELLAS</span>
          </div>
        )}
      </div>
      <nav className="space-y-1">
        {navItems.filter(item => item.roles.includes(user?.role)).map(item => (
          <button key={item.id} onClick={() => { setActiveTab(item.id); if (item.id === "chat") setChatUnread(0); }} className={`nav-item w-full text-left ${activeTab === item.id ? 'active' : ''}`} data-testid={`nav-${item.id}`}>
            <item.icon size={20} weight="duotone" /> {item.label}
            {item.badge > 0 && (
              <span className="ml-auto bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{item.badge > 99 ? "99+" : item.badge}</span>
            )}
          </button>
        ))}

        {/* Administracion - desplegable para admin y auxiliar (incluye POS) */}
        {['admin', 'auxiliar'].includes(user?.role) && (
          <>
            <button onClick={() => setAdminOpen(!adminOpen)} className={`nav-item w-full text-left ${isAdminTab || isPosTab ? 'active' : ''}`} data-testid="nav-admin-toggle">
              <Buildings size={20} weight="duotone" /> <span className="flex-1">Administracion</span>
              <CaretRight size={14} className={`transition-transform duration-200 ${adminOpen || isAdminTab || isPosTab ? 'rotate-90' : ''}`} />
            </button>
            {(adminOpen || isAdminTab || isPosTab) && (
              <div className="ml-4 space-y-0.5 border-l border-slate-700 pl-3">
                {adminItems.map(item => (
                  <button key={item.id} onClick={() => setActiveTab(item.id)} className={`nav-item w-full text-left text-sm py-2 ${activeTab === item.id ? 'active' : ''}`} data-testid={`nav-${item.id}`}>
                    <item.icon size={16} weight="duotone" /> {item.label}
                  </button>
                ))}
                {/* POS sub-section inside Administracion */}
                <button onClick={() => setPosOpen(!posOpen)} className={`nav-item w-full text-left text-sm py-2 ${isPosTab ? 'active' : ''}`} data-testid="nav-pos-toggle">
                  <Storefront size={16} weight="duotone" /> <span className="flex-1">Punto de Venta</span>
                  <CaretRight size={12} className={`transition-transform duration-200 ${posOpen || isPosTab ? 'rotate-90' : ''}`} />
                </button>
                {(posOpen || isPosTab) && (
                  <div className="ml-3 space-y-0.5 border-l border-slate-700/50 pl-2">
                    {posItems.map(item => (
                      <button key={item.id} onClick={() => setActiveTab(item.id)} className={`nav-item w-full text-left text-xs py-1.5 ${activeTab === item.id ? 'active' : ''}`} data-testid={`nav-${item.id}`}>
                        <item.icon size={14} weight="duotone" /> {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Administracion for non-admin/auxiliar POS users */}
        {['comercial', 'ingeniero', 'subgerente'].includes(user?.role) && (
          <>
            <button onClick={() => setPosOpen(!posOpen)} className={`nav-item w-full text-left ${isPosTab ? 'active' : ''}`} data-testid="nav-pos-toggle">
              <Storefront size={20} weight="duotone" /> <span className="flex-1">Punto de Venta</span>
              <CaretRight size={14} className={`transition-transform duration-200 ${posOpen || isPosTab ? 'rotate-90' : ''}`} />
            </button>
            {(posOpen || isPosTab) && (
              <div className="ml-4 space-y-0.5 border-l border-slate-700 pl-3">
                {posItems.map(item => (
                  <button key={item.id} onClick={() => setActiveTab(item.id)} className={`nav-item w-full text-left text-sm py-2 ${activeTab === item.id ? 'active' : ''}`} data-testid={`nav-${item.id}`}>
                    <item.icon size={16} weight="duotone" /> {item.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {user?.role === 'admin' && (
          <>
            <button onClick={() => setConfigOpen(!configOpen)} className={`nav-item w-full text-left ${isConfigTab ? 'active' : ''}`} data-testid="nav-config-toggle">
              <GearSix size={20} weight="duotone" /> <span className="flex-1">Configuracion</span>
              <CaretRight size={14} className={`transition-transform duration-200 ${configOpen || isConfigTab ? 'rotate-90' : ''}`} />
            </button>
            {(configOpen || isConfigTab) && (
              <div className="ml-4 space-y-0.5 border-l border-slate-700 pl-3">
                {configItems.map(item => (
                  <button key={item.id} onClick={() => setActiveTab(item.id)} className={`nav-item w-full text-left text-sm py-2 ${activeTab === item.id ? 'active' : ''}`} data-testid={`nav-${item.id}`}>
                    <item.icon size={16} weight="duotone" /> {item.label}
                  </button>
                ))}
              </div>
            )}
            {/* Servicios - oculto temporalmente */}
          </>
        )}
      </nav>
      <div className="mt-4 px-6 pb-6">
        <button onClick={handleLogout} className="nav-item w-full text-left hover:bg-red-900/30 hover:text-red-400" data-testid="logout-button">
          <SignOut size={20} weight="duotone" /> Cerrar Sesion
        </button>
      </div>
    </aside>
  );
};
